import { CALCULATION_METHODS, EF_BASES, FACTOR_GASES, GWP_PRESETS } from "../../model/options";
import { deriveFactorValue, factorGasContributions, hasGasBreakdown, needsDensity } from "../../model/helpers";
import { KG_CO2E } from "../../../../../_constants/emissionUnits";

/** @param {number|null} value @returns {string} */
const formatKg = (value) => (value == null ? "—" : Number(value.toFixed(6)).toString());

const GWP_KEYS = ["gwpCo2", "gwpCh4", "gwpN2o"];

/**
 * The preset whose source and GWPs all equal the factor's, or "" when the
 * factor's GWPs were typed or edited after a preset was applied.
 *
 * @param {import("../../model/helpers").GridFactor} factor
 * @returns {string}
 */
const matchingPreset = (factor) =>
  GWP_PRESETS.find(
    (preset) =>
      preset.source === factor.gwpSource &&
      GWP_KEYS.every((key) => factor[key] !== "" && Number(factor[key]) === Number(preset[key]))
  )?.source ?? "";

/**
 * Copies a preset's source and GWPs onto the factor. Picking "Custom" leaves
 * the factor as it is.
 *
 * @param {string} source
 * @param {(patch: Partial<import("../../model/helpers").GridFactor>) => void} onChange
 */
const applyPreset = (source, onChange) => {
  const preset = GWP_PRESETS.find((candidate) => candidate.source === source);
  if (!preset) return;
  const { source: gwpSource, ...values } = preset;
  onChange({ gwpSource, ...values });
};

/**
 * How one factor is built gas by gas: the method, the basis the gas values are
 * quoted in, density and NCV for fuel energy, then a factor and a GWP per gas.
 * Each gas's CO2e share and the total are recalculated as the user types. A
 * mass unit has no density field: the unit already is the kg.
 *
 * @param {object} props
 * @param {import("../../model/helpers").GridFactor} props.factor
 * @param {string} props.unitName the unit the factor is quoted per
 * @param {(patch: Partial<import("../../model/helpers").GridFactor>) => void} props.onChange
 */
export default function FactorBreakdownFields({ factor, unitName, onChange }) {
  const fuelEnergy = factor.method === "FUEL_ENERGY";
  const typedDensity = needsDensity(factor);
  const co2eBasis = factor.basis === "CO2E";
  const contributions = factorGasContributions(factor);
  const efUnit = fuelEnergy ? "kg / TJ" : `kg / ${unitName}`;

  const numberInput = (key, label, placeholder) => (
    <input
      type="number"
      min="0"
      step="any"
      className="kgb-breakdown-number"
      value={factor[key]}
      placeholder={placeholder}
      aria-label={label}
      onChange={(e) => onChange({ [key]: e.target.value })}
    />
  );

  return (
    <div className="kgb-breakdown">
      <div className="kgb-breakdown-line">
        <label className="kgb-breakdown-field">
          <span>Method</span>
          <select value={factor.method} onChange={(e) => onChange({ method: e.target.value })}>
            {CALCULATION_METHODS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <label className="kgb-breakdown-field">
          <span>Gas values are</span>
          <select value={factor.basis} onChange={(e) => onChange({ basis: e.target.value })}>
            {EF_BASES.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        {fuelEnergy ? (
          <>
            {typedDensity ? (
              <label className="kgb-breakdown-field">
                <span>Density (kg / {unitName})</span>
                {numberInput("density", "Density", "0.832")}
              </label>
            ) : null}
            <label className="kgb-breakdown-field">
              <span>NCV (TJ / Gg)</span>
              {numberInput("ncv", "Net calorific value", "43.0")}
            </label>
          </>
        ) : null}
      </div>

      <table className="kgb-breakdown-gases">
        <thead>
          <tr>
            <th scope="col">Gas</th>
            <th scope="col">
              Factor (<span style={{ textTransform: "none" }}>{co2eBasis ? `${KG_CO2E} / ${fuelEnergy ? "TJ" : unitName}` : efUnit}</span>)
            </th>
            <th scope="col">GWP{co2eBasis ? " (reference)" : ""}</th>
            <th scope="col"><span style={{ textTransform: "none" }}>kg / {unitName}</span></th>
          </tr>
        </thead>
        <tbody>
          {FACTOR_GASES.map(({ gas, label, ef, gwp }) => (
            <tr key={gas}>
              <th scope="row">{label}</th>
              <td>{numberInput(ef, `${label} factor`, "")}</td>
              <td>{numberInput(gwp, `${label} GWP`, "")}</td>
              <td className="kgb-breakdown-result">{formatKg(contributions[gas])}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="kgb-breakdown-line">
        <label className="kgb-breakdown-field kgb-breakdown-field--grow">
          <span>GWP source</span>
          <input
            type="text"
            maxLength={50}
            value={factor.gwpSource}
            placeholder="IPCC AR6"
            onChange={(e) => onChange({ gwpSource: e.target.value })}
          />
        </label>
        <label className="kgb-breakdown-field">
          <span>GWP preset</span>
          <select value={matchingPreset(factor)} onChange={(e) => applyPreset(e.target.value, onChange)}>
            <option value="">Custom</option>
            {GWP_PRESETS.map(({ source, gwpCh4, gwpN2o }) => (
              <option key={source} value={source}>
                {source} (CH₄ {gwpCh4}, N₂O {gwpN2o})
              </option>
            ))}
          </select>
        </label>
        <span className="kgb-breakdown-total">
          {hasGasBreakdown(factor)
            ? `= ${formatKg(deriveFactorValue(factor))} ${KG_CO2E} / ${unitName}`
            : "No gas values yet"}
        </span>
      </div>
    </div>
  );
}
